import { FC, memo, useMemo } from "react";
import { BalanceContainer, BalanceInfo, BalanceTitle, ButtonsContainer, PaymentCompositeValue, PaymentMethod, PaymentMethodBottom, PaymentMethodCheckbox, PaymentMethodHeader, PaymentMethodTitle, PaymentValue, RectButtonWrapper } from "../styles";
import { HintTooltip, useBreakpoint, useIsMobile } from "@brg/ui";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AddButtonIcon } from "../assets/AddButton";
import { QuestionButtonIcon } from "../assets/QuestionButton";
import { LayoutConst, useModal } from "core";
import { CurrencyAccountType, CurrencyTheme, getCurrencyIcon, getCurrencyType, getSortedCompositeCurrencyParts, isCompositeCurrency, searchCurrencyItem } from "../helpers/utils";
import { CurrencyType } from "@brg/api";
import { AppTester, getCurrentContextName } from "supporting";
import { isServer } from "shared/constants/isServer";
import { FlowType } from "../../../constants/flowType";
import { PaymentCurrency, UserPaymentCurrency } from "../../../constants/types";
import { selectorUserCurrencies } from "../../../slices/userCurrenciesSlice";
import { useCurrency } from "../../../hooks/useCurrency";
import { changeCurrencyType, selectorOrder, setImmediatePurchaseProps } from "../../../slices/OrderSlice";
import { getTooltipStyles, tooptipStylesWhite } from "shared/constants/tooptipStyles";
import { useTheme } from "styled-components";
import { InfoIcon } from "shared/components/InfoIcon/InfoIcon";

export interface PaymentMethodComponentProps {
    paymentItem: PaymentCurrency
    selectedCurrency: PaymentCurrency
    setSelectedCurrency: (value: PaymentCurrency) => void
    currencyTheme?: CurrencyTheme
    competitionId?: string // ID конкурса для COPY_CARD flow
    selectedProjectId?: string // ID выбранного проекта для COPY_CARD flow
}


export const PaymentMethodComponent: FC<PaymentMethodComponentProps> = memo((props) => {
    const { paymentItem, selectedCurrency, setSelectedCurrency, currencyTheme = CurrencyTheme.dark, competitionId, selectedProjectId } = props
    const { t, i18n } = useTranslation()
    const dispatch = useDispatch()
    const isMobile = useIsMobile();
    const theme = useTheme();
    const order = useSelector(selectorOrder)
    const userBalance = useSelector(selectorUserCurrencies)
    const modal = useModal()
    const { formatValue } = useCurrency()
    const context = getCurrentContextName()
    const isDeziiignBooks = AppTester.isBooksMain()
    const is1440BP = useBreakpoint(LayoutConst.screen.mac13)
    const itemCurrencyType = getCurrencyType(paymentItem)
    const selectedCurrencyType = getCurrencyType(selectedCurrency)

    const changePaymentMethod = (paymentCurrency: PaymentCurrency) => async (e: any) => {
        console.log('PaymentMethodComponent', { paymentCurrency, e });
        const { target } = e || {}
        const isString = (value: any) => typeof value === 'string' || value instanceof String;

        const className = isString(target?.className) ? target?.className : '';
        const parentClassName = isString(target?.parentElement?.className) ? target?.parentElement?.className : '';
        const grandParentClassName = isString(target?.parentElement?.parentElement?.className) ? target?.parentElement?.parentElement?.className : '';
        if (className?.includes('addButton') || parentClassName?.includes('addButton') || grandParentClassName?.includes('addButton')) {
            return
        }

        
        setSelectedCurrency(paymentCurrency)
        dispatch(changeCurrencyType(getCurrencyType(paymentCurrency)))
        dispatch(setImmediatePurchaseProps({
            ...order.immediatePurchaseProps,
            currencyType: getCurrencyType(paymentCurrency),
            price: isCompositeCurrency(paymentCurrency) ? getSortedCompositeCurrencyParts(paymentCurrency)[0].amount : paymentCurrency?.amount,
        }))
    }

    const userCurrentCurrency = useMemo(
        () => {
            if (!userBalance.currencies) {
                return 0
            }

            const userCurrency = searchCurrencyItem({
                accountType: CurrencyAccountType.user,
                currencies: userBalance?.currencies,
                search: {
                    currencyType: getCurrencyType(paymentItem)
                }
            }) as UserPaymentCurrency | undefined

            return formatValue(userCurrency?.amount || 0)
        },
        [paymentItem, userBalance, formatValue, context]
    )

    const currencyIcon = useMemo(() => {
        const isWhite = selectedCurrencyType === itemCurrencyType
        return getCurrencyIcon({ currencyType: getCurrencyType(paymentItem), isWhite })
    }, [paymentItem, selectedCurrencyType, itemCurrencyType]) 
    const secondaryCurrencyIcon = useMemo(() => {
        if (isCompositeCurrency(paymentItem) && paymentItem.parts.length > 1) {
            return getCurrencyIcon({ currencyType: getSortedCompositeCurrencyParts(paymentItem)[1].currencyType })
        }
        return undefined
    }, [paymentItem, selectedCurrencyType, itemCurrencyType])

    const currencyPrice = useMemo(() => {
        if (isCompositeCurrency(paymentItem)) {
            return formatValue(getSortedCompositeCurrencyParts(paymentItem)[0].amount || undefined)
        }
        return paymentItem?.amount
    }, [paymentItem])
    const secondaryCurrencyPrice = useMemo(() => {
        if (isCompositeCurrency(paymentItem) && paymentItem.parts.length > 1) {
            return formatValue(getSortedCompositeCurrencyParts(paymentItem)[1].amount || undefined)
        }
        return 0
    }, [paymentItem])

    const currencyBalanceIcon = useMemo(() => {
        const isWhite = selectedCurrencyType === itemCurrencyType
        return getCurrencyIcon({ currencyType: getCurrencyType(paymentItem), isBalance: true, isWhite })
    }, [paymentItem, selectedCurrencyType, itemCurrencyType])
    const currencyTitle: string = useMemo(() => {
        const currencyName = t(`payment.currency_title.${itemCurrencyType}`)
        return currencyName || "";
    }, [i18n.language, t, itemCurrencyType])

    const onAddClick = () => {
        if (isServer) {
            return
        }
        if (order?.immediatePurchaseProps?.flowType === FlowType.COPY_BOOK) {
            const savedOrder = JSON.stringify(order)
            localStorage.setItem(`${FlowType.COPY_BOOK}_${order?.immediatePurchaseProps.productId}`, savedOrder || '')
        }
        if (order?.immediatePurchaseProps?.flowType === FlowType.BUY_BOOK) {
            const savedOrder = JSON.stringify(order)
            localStorage.setItem(`${FlowType.BUY_BOOK}_${order?.immediatePurchaseProps.productId}`, savedOrder || '')
        }
        if (order?.immediatePurchaseProps?.flowType === FlowType.COPY_CARD) {
            const savedOrder = JSON.stringify(order)
            localStorage.setItem(`${FlowType.COPY_CARD}_${order?.immediatePurchaseProps.productId}`, savedOrder || '')
        }
        modal.hide();
        modal.show('topUpBalance', {
            isOpen: true,
            containerStyles: {
                background: 'rgba(231,231,231, 1)',
                backdropFilter: 'none'
            },
            competitionId: competitionId,
            selectedProjectId: selectedProjectId
        })

    }

    return <PaymentMethod
        isSelected={selectedCurrencyType === itemCurrencyType}
        currencyTheme={currencyTheme}
        isGift={true}
        onClick={changePaymentMethod(paymentItem)}
    >
        <PaymentMethodHeader>
            <PaymentMethodTitle type="real">
                {currencyTitle}
            </PaymentMethodTitle>
            {order.paymentCurrencies?.length > 1 ? <PaymentMethodCheckbox 
                selected={selectedCurrencyType === itemCurrencyType}
            /> : <></>}
        </PaymentMethodHeader>
        <PaymentMethodBottom mobile={isMobile}>
            <BalanceContainer>
                <BalanceInfo>
                    {(itemCurrencyType !== CurrencyType.RUB) && ( 
                        <>
                            <BalanceTitle>{t('payment.your_balance')}</BalanceTitle>
                            <PaymentValue type="real">
                                {userCurrentCurrency} {currencyBalanceIcon}
                            </PaymentValue>
                        </>
                    )}
                </BalanceInfo>
                <BalanceInfo>
                    <ButtonsContainer>
                            {itemCurrencyType === CurrencyType.RealMoney &&
                                <HintTooltip
                                    //@ts-ignore
                                    customStyle={isDeziiignBooks ? tooptipStylesWhite : getTooltipStyles(theme as any)}
                                    text={t("payment.payment_method.top_up")}
                                    positionY="top"
                                    textOffset={!is1440BP ? "calc(-17.5% + 8px)" : ""}
                                >
                                    <RectButtonWrapper dark={selectedCurrencyType !== itemCurrencyType} className="addButton" onClick={onAddClick} >
                                        <AddButtonIcon />
                                    </RectButtonWrapper>
                                </HintTooltip>
                            }
                            {isMobile ? (
                                <InfoIcon 
                                    squareIcon 
                                    msg={t(`payment.payment_method.${itemCurrencyType !== CurrencyType.RealMoney ? 'florin_info' : 'zephyr_info'}`)} 
                                    stc 
                                />
                            ) : (
                                itemCurrencyType !== CurrencyType.RUB && <HintTooltip
                                //@ts-ignore
                                    customStyle={isDeziiignBooks ? tooptipStylesWhite : getTooltipStyles(theme as any)}
                                    positionY={'top'}
                                    text={t(`payment.payment_method.${itemCurrencyType !== CurrencyType.RealMoney ? 'florin_info' : 'zephyr_info'}`)} 
                                    textOffset={
                                        !is1440BP ? 
                                        `calc(${itemCurrencyType === CurrencyType.RealMoney ? '-17.5%' : '-7.5%'} + 8px)` : 
                                        `${itemCurrencyType === CurrencyType.RealMoney ? '-48px' : '-17px'}`
                                    }
                                >
                                    <RectButtonWrapper dark={selectedCurrencyType !== itemCurrencyType}>
                                        <QuestionButtonIcon />
                                    </RectButtonWrapper>
                                </HintTooltip>
                            )}
                    </ButtonsContainer>
                </BalanceInfo>
            </BalanceContainer>
            {
                isCompositeCurrency(paymentItem) ?
                <PaymentCompositeValue>
                    {paymentItem.parts.length > 1 ? 
                        <PaymentValue type="real">
                            +{secondaryCurrencyPrice} {secondaryCurrencyIcon}
                        </PaymentValue> : <></>
                    }
                    <PaymentValue big type="real">
                        {currencyPrice}  {currencyIcon}
                    </PaymentValue>
                </PaymentCompositeValue> :
                <PaymentValue big type="real">
                    {currencyPrice}  {currencyIcon}
                </PaymentValue>
            }
        </PaymentMethodBottom>
    </PaymentMethod>
})
import { FC, memo, useMemo } from "react";
import { BalanceContainer, BalanceInfo, BalanceTitle, ButtonsContainer, PaymentCompositeValue, PaymentMethod, PaymentMethodBottom, PaymentMethodCheckbox, PaymentMethodHeader, PaymentMethodTitle, PaymentValue, RectButtonWrapper } from "../styles";
import { HintTooltip, useBreakpoint, useIsMobile } from "@brg/ui";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AddButtonIcon } from "../assets/AddButton";
import { QuestionButtonIcon } from "../assets/QuestionButton";
import { LayoutConst, useModal } from "core";
import { CurrencyAccountType, CurrencyTheme, getCurrencyIcon, getCurrencyType, getSortedCompositeCurrencyParts, isCompositeCurrency, searchCurrencyItem } from "../helpers/utils";
import { CurrencyType } from "@brg/api";
import { AppTester, getCurrentContextName } from "supporting";
import { isServer } from "shared/constants/isServer";
import { FlowType } from "../../../constants/flowType";
import { PaymentCurrency, UserPaymentCurrency } from "../../../constants/types";
import { selectorUserCurrencies } from "../../../slices/userCurrenciesSlice";
import { useCurrency } from "../../../hooks/useCurrency";
import { changeCurrencyType, selectorOrder, setImmediatePurchaseProps } from "../../../slices/OrderSlice";
import { getTooltipStyles, tooptipStylesWhite } from "shared/constants/tooptipStyles";
import { useTheme } from "styled-components";
import { InfoIcon } from "shared/components/InfoIcon/InfoIcon";

export interface PaymentMethodComponentProps {
    paymentItem: PaymentCurrency
    selectedCurrency: PaymentCurrency
    setSelectedCurrency: (value: PaymentCurrency) => void
    currencyTheme?: CurrencyTheme
    competitionId?: string // ID конкурса для COPY_CARD flow
    selectedProjectId?: string // ID выбранного проекта для COPY_CARD flow
}


export const PaymentMethodComponent: FC<PaymentMethodComponentProps> = memo((props) => {
    const { paymentItem, selectedCurrency, setSelectedCurrency, currencyTheme = CurrencyTheme.dark, competitionId, selectedProjectId } = props
    const { t, i18n } = useTranslation()
    const dispatch = useDispatch()
    const isMobile = useIsMobile();
    const theme = useTheme();
    const order = useSelector(selectorOrder)
    const userBalance = useSelector(selectorUserCurrencies)
    const modal = useModal()
    const { formatValue } = useCurrency()
    const context = getCurrentContextName()
    const isDeziiignBooks = AppTester.isBooksMain()
    const is1440BP = useBreakpoint(LayoutConst.screen.mac13)
    const itemCurrencyType = getCurrencyType(paymentItem)
    const selectedCurrencyType = getCurrencyType(selectedCurrency)

    const changePaymentMethod = (paymentCurrency: PaymentCurrency) => async (e: any) => {
        console.log('PaymentMethodComponent', { paymentCurrency, e });
        const { target } = e || {}
        const isString = (value: any) => typeof value === 'string' || value instanceof String;

        const className = isString(target?.className) ? target?.className : '';
        const parentClassName = isString(target?.parentElement?.className) ? target?.parentElement?.className : '';
        const grandParentClassName = isString(target?.parentElement?.parentElement?.className) ? target?.parentElement?.parentElement?.className : '';
        if (className?.includes('addButton') || parentClassName?.includes('addButton') || grandParentClassName?.includes('addButton')) {
            return
        }

        
        setSelectedCurrency(paymentCurrency)
        dispatch(changeCurrencyType(getCurrencyType(paymentCurrency)))
        dispatch(setImmediatePurchaseProps({
            ...order.immediatePurchaseProps,
            currencyType: getCurrencyType(paymentCurrency),
            price: isCompositeCurrency(paymentCurrency) ? getSortedCompositeCurrencyParts(paymentCurrency)[0].amount : paymentCurrency?.amount,
        }))
    }

    const userCurrentCurrency = useMemo(
        () => {
            if (!userBalance.currencies) {
                return 0
            }

            const userCurrency = searchCurrencyItem({
                accountType: CurrencyAccountType.user,
                currencies: userBalance?.currencies,
                search: {
                    currencyType: getCurrencyType(paymentItem)
                }
            }) as UserPaymentCurrency | undefined

            return formatValue(userCurrency?.amount || 0)
        },
        [paymentItem, userBalance, formatValue, context]
    )

    const currencyIcon = useMemo(() => {
        const isWhite = selectedCurrencyType === itemCurrencyType
        return getCurrencyIcon({ currencyType: getCurrencyType(paymentItem), isWhite })
    }, [paymentItem, selectedCurrencyType, itemCurrencyType]) 
    const secondaryCurrencyIcon = useMemo(() => {
        if (isCompositeCurrency(paymentItem) && paymentItem.parts.length > 1) {
            return getCurrencyIcon({ currencyType: getSortedCompositeCurrencyParts(paymentItem)[1].currencyType })
        }
        return undefined
    }, [paymentItem, selectedCurrencyType, itemCurrencyType])

    const currencyPrice = useMemo(() => {
        if (isCompositeCurrency(paymentItem)) {
            return formatValue(getSortedCompositeCurrencyParts(paymentItem)[0].amount || undefined)
        }
        return paymentItem?.amount
    }, [paymentItem])
    const secondaryCurrencyPrice = useMemo(() => {
        if (isCompositeCurrency(paymentItem) && paymentItem.parts.length > 1) {
            return formatValue(getSortedCompositeCurrencyParts(paymentItem)[1].amount || undefined)
        }
        return 0
    }, [paymentItem])

    const currencyBalanceIcon = useMemo(() => {
        const isWhite = selectedCurrencyType === itemCurrencyType
        return getCurrencyIcon({ currencyType: getCurrencyType(paymentItem), isBalance: true, isWhite })
    }, [paymentItem, selectedCurrencyType, itemCurrencyType])
    const currencyTitle: string = useMemo(() => {
        const currencyName = t(`payment.currency_title.${itemCurrencyType}`)
        return currencyName || "";
    }, [i18n.language, t, itemCurrencyType])

    const onAddClick = () => {
        if (isServer) {
            return
        }
        if (order?.immediatePurchaseProps?.flowType === FlowType.COPY_BOOK) {
            const savedOrder = JSON.stringify(order)
            localStorage.setItem(`${FlowType.COPY_BOOK}_${order?.immediatePurchaseProps.productId}`, savedOrder || '')
        }
        if (order?.immediatePurchaseProps?.flowType === FlowType.BUY_BOOK) {
            const savedOrder = JSON.stringify(order)
            localStorage.setItem(`${FlowType.BUY_BOOK}_${order?.immediatePurchaseProps.productId}`, savedOrder || '')
        }
        if (order?.immediatePurchaseProps?.flowType === FlowType.COPY_CARD) {
            const savedOrder = JSON.stringify(order)
            localStorage.setItem(`${FlowType.COPY_CARD}_${order?.immediatePurchaseProps.productId}`, savedOrder || '')
        }
        modal.hide();
        modal.show('topUpBalance', {
            isOpen: true,
            containerStyles: {
                background: 'rgba(231,231,231, 1)',
                backdropFilter: 'none'
            },
            competitionId: competitionId,
            selectedProjectId: selectedProjectId
        })

    }

    return <PaymentMethod
        isSelected={selectedCurrencyType === itemCurrencyType}
        currencyTheme={currencyTheme}
        isGift={true}
        onClick={changePaymentMethod(paymentItem)}
    >
        <PaymentMethodHeader>
            <PaymentMethodTitle type="real">
                {currencyTitle}
            </PaymentMethodTitle>
            {order.paymentCurrencies?.length > 1 ? <PaymentMethodCheckbox 
                selected={selectedCurrencyType === itemCurrencyType}
            /> : <></>}
        </PaymentMethodHeader>
        <PaymentMethodBottom mobile={isMobile}>
            <BalanceContainer>
                <BalanceInfo>
                    {(itemCurrencyType !== CurrencyType.RUB) && ( 
                        <>
                            <BalanceTitle>{t('payment.your_balance')}</BalanceTitle>
                            <PaymentValue type="real">
                                {userCurrentCurrency} {currencyBalanceIcon}
                            </PaymentValue>
                        </>
                    )}
                </BalanceInfo>
                <BalanceInfo>
                    <ButtonsContainer>
                            {itemCurrencyType === CurrencyType.RealMoney &&
                                <HintTooltip
                                    //@ts-ignore
                                    customStyle={isDeziiignBooks ? tooptipStylesWhite : getTooltipStyles(theme as any)}
                                    text={t("payment.payment_method.top_up")}
                                    positionY="top"
                                    textOffset={!is1440BP ? "calc(-17.5% + 8px)" : ""}
                                >
                                    <RectButtonWrapper dark={selectedCurrencyType !== itemCurrencyType} className="addButton" onClick={onAddClick} >
                                        <AddButtonIcon />
                                    </RectButtonWrapper>
                                </HintTooltip>
                            }
                            {isMobile ? (
                                <InfoIcon 
                                    squareIcon 
                                    msg={t(`payment.payment_method.${itemCurrencyType !== CurrencyType.RealMoney ? 'florin_info' : 'zephyr_info'}`)} 
                                    stc 
                                />
                            ) : (
                                itemCurrencyType !== CurrencyType.RUB && <HintTooltip
                                //@ts-ignore
                                    customStyle={isDeziiignBooks ? tooptipStylesWhite : getTooltipStyles(theme as any)}
                                    positionY={'top'}
                                    text={t(`payment.payment_method.${itemCurrencyType !== CurrencyType.RealMoney ? 'florin_info' : 'zephyr_info'}`)} 
                                    textOffset={
                                        !is1440BP ? 
                                        `calc(${itemCurrencyType === CurrencyType.RealMoney ? '-17.5%' : '-7.5%'} + 8px)` : 
                                        `${itemCurrencyType === CurrencyType.RealMoney ? '-48px' : '-17px'}`
                                    }
                                >
                                    <RectButtonWrapper dark={selectedCurrencyType !== itemCurrencyType}>
                                        <QuestionButtonIcon />
                                    </RectButtonWrapper>
                                </HintTooltip>
                            )}
                    </ButtonsContainer>
                </BalanceInfo>
            </BalanceContainer>
            {
                isCompositeCurrency(paymentItem) ?
                <PaymentCompositeValue>
                    {paymentItem.parts.length > 1 ? 
                        <PaymentValue type="real">
                            +{secondaryCurrencyPrice} {secondaryCurrencyIcon}
                        </PaymentValue> : <></>
                    }
                    <PaymentValue big type="real">
                        {currencyPrice}  {currencyIcon}
                    </PaymentValue>
                </PaymentCompositeValue> :
                <PaymentValue big type="real">
                    {currencyPrice}  {currencyIcon}
                </PaymentValue>
            }
        </PaymentMethodBottom>
    </PaymentMethod>
})
